import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { ApiService } from '../services/api.service';
import { CARODocument, CaroDocumentType } from '../models/document.model';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { ShowService } from './show.service';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {
    private readonly showService: ShowService = inject(ShowService);

    constructor(private http: HttpClient, private readonly api: ApiService, private readonly platform: Platform) {
    }

    public async getById(id: number): Promise<CARODocument> {
        return Object.assign(
            new CARODocument(),
            await this.api.getById<CARODocument>(`documents`, id)
        );
    }

    public async getAllProtocol(): Promise<CARODocument[]> {
        const result = await this.api.getAll<CARODocument>(`documents/protocol`);

        result.forEach(document => {
            Object.assign(new CARODocument(), document);
        });

        return result;
    }

    public delete(documentId: number) {

        this.http.put(
            `${ environment.apiUrl }/documents/unpublish/${ documentId }`,
            null
        ).subscribe(
            data => {
                console.log('[DocumentService] delete() - response: ', data);
            },
            error => {
                console.warn('APP [DocumentService] delete() - error:', error);
            }
        );

    }

    public async uploadDocument(base64String: string, performanceId: number, documentType: CaroDocumentType, documentName: string): Promise<any> {
        console.log('APP - [DocumentService] uploadDocument() - performanceId: ', performanceId);

        const showId = await this.showService.getActiveShowId();
        const apiUrl = documentType === CaroDocumentType.eftelingshowreport            ? `${ environment.apiUrl }/performances/upload-eftelingshowreport`            : `${ environment.apiUrl }/documents/upload`;

        this.http.post(apiUrl, {
            base64: base64String,
            performanceId: performanceId,
            showId: showId,
            documentType: documentType,
            documentName: documentName
        }).subscribe(
            data => {
                console.log('[DocumentService] uploadDocument() - response: ', data);
                return data;
            },
            error => {
                console.warn('APP [DocumentService] uploadDocument() - error:', error);
            }
        );
    }

    public async downloadById(id: number) {
        const url = `${ environment.apiUrl }/documents/download/${ id }`;
        window.open(url, '_blank');
    }

    public async downloadDocumentWithToken(token: string): Promise<void> {
        const requestUrl = `${ environment.apiUrl }/documents/download/${ token }`;

        console.log('requestUrl: ' + requestUrl);

        // 1. Platform-check
        const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android' | ...
        const isWeb = platform === 'web';

        try {
            // 2. Download PDF als Blob
            const response = await this.http.get(requestUrl, {
                responseType: 'blob',
                observe: 'response'
            }).toPromise();

            if (!response || !response.body) {
                throw new Error('Geen data ontvangen van de server');
            }

            // 3. Bepaal bestandsnaam uit headers
            const cd = response.headers.get('content-disposition');
            const filename = cd                ? /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd)?.[1]
                ?.replace(/['"]/g, '')
                .replace(/^UTF-8/, '') || 'document.pdf'                : 'document.pdf';

            console.log('Trying to download file with filename: ' + filename);

            if (isWeb) {
                console.log('Web detected - Download via web method');
                // ———  Web/desktop: Blob + <a download> —————————————————————
                const blob = new Blob([response.body], {type: 'application/pdf'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

            } else {
                console.log('Native device detected - Download via capacitor plugin method');
                this.openPdfInBrowser(requestUrl);
                //     // ———  Native (Android/iOS): Filesystem + FileOpener ——————————
                //     // Converteer Blob naar Base64
                //     const base64 = await this.blobToBase64(response.body);
                //
                //     // Schrijf bestand naar cache
                //     const writeResult = await Filesystem.writeFile({
                //         path: filename,
                //         data: base64.split(',')[1],
                //         directory: Directory.Cache
                //     });
                //
                //     console.log(writeResult);
                //     console.log('now trying to use FileOpener.open');
                //
                //     // Open in native PDF-viewer
                //     await FileOpener.open({
                //         filePath: writeResult.uri,
                //         contentType: 'application/pdf',
                //         openWithDefault: true
                //     });
            }

        } catch (error) {
            console.error('Fout bij het downloaden/openen van het document:', error);
            throw error;
        }
    }

    private async openPdfInBrowser(url: string): Promise<void> {
        try {
            // Browser.open opent een in-app webview (capacitor/browser)
            await Browser.open({
                url,
                // optioneel: fullscreen, toolbar-kleur, etc.
                presentationStyle: 'popover'
            });
        } catch (err) {
            console.error('Fout bij openen PDF in Browser-plugin:', err);
            throw err;
        }
    }

    private async blobToBase64(blob: Blob): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

}
